import { supabase } from '../lib/supabase';

/**
 * Application History Service
 * Handles application analytics, interview scheduling, notes, reminders, and comparison features
 */

// =====================================================
// APPLICATION ANALYTICS
// =====================================================

/**
 * Get application analytics for a user
 */
export const getApplicationAnalytics = async (userId) => {
  try {
    const { data, error } = await supabase?.from('application_analytics')?.select('*')?.eq('user_id', userId)?.single();

    if (error && error?.code !== 'PGRST116') throw error;

    // If no analytics exist, calculate them
    if (!data) {
      await calculateApplicationAnalytics(userId);
      return await getApplicationAnalytics(userId);
    }

    return {
      totalApplications: data?.total_applications,
      applicationsThisWeek: data?.applications_this_week,
      applicationsThisMonth: data?.applications_this_month,
      responseRate: data?.response_rate,
      interviewRate: data?.interview_rate,
      offerRate: data?.offer_rate,
      avgResponseTimeDays: data?.avg_response_time_days,
      fastestResponseDays: data?.fastest_response_days,
      slowestResponseDays: data?.slowest_response_days,
      lastCalculatedAt: data?.last_calculated_at
    };
  } catch (error) {
    console.error('Error fetching application analytics:', error);
    throw error;
  }
};

/**
 * Calculate/recalculate application analytics for a user
 */
export const calculateApplicationAnalytics = async (userId) => {
  try {
    const { error } = await supabase?.rpc('calculate_application_analytics', {
      p_user_id: userId
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error calculating application analytics:', error);
    throw error;
  }
};

// =====================================================
// INTERVIEW SCHEDULES
// =====================================================

/**
 * Get all interview schedules for a user
 */
export const getInterviewSchedules = async (userId, options = {}) => {
  try {
    let query = supabase?.from('interview_schedules')?.select(`
        *,
        job_applications!inner(
          id,
          status,
          applied_at,
          jobs!inner(
            id,
            title,
            companies!inner(
              id,
              name,
              logo_url
            ),
            company_locations!inner(
              city,
              state
            )
          )
        )
      `)?.eq('user_id', userId);

    // Filter by status if provided
    if (options?.status) {
      query = query?.eq('status', options?.status);
    }

    // Filter by date range
    if (options?.startDate) {
      query = query?.gte('interview_date', options?.startDate);
    }
    if (options?.endDate) {
      query = query?.lte('interview_date', options?.endDate);
    }

    // Sort by interview date
    query = query?.order('interview_date', { ascending: options?.ascending !== false });

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(schedule => ({
      id: schedule?.id,
      applicationId: schedule?.application_id,
      interviewType: schedule?.interview_type,
      interviewDate: schedule?.interview_date,
      interviewDurationMinutes: schedule?.interview_duration_minutes,
      location: schedule?.location,
      meetingLink: schedule?.meeting_link,
      meetingPassword: schedule?.meeting_password,
      interviewerName: schedule?.interviewer_name,
      interviewerEmail: schedule?.interviewer_email,
      interviewerTitle: schedule?.interviewer_title,
      preparationNotes: schedule?.preparation_notes,
      interviewNotes: schedule?.interview_notes,
      outcome: schedule?.outcome,
      calendarEventId: schedule?.calendar_event_id,
      calendarProvider: schedule?.calendar_provider,
      reminderSent: schedule?.reminder_sent,
      reminderSentAt: schedule?.reminder_sent_at,
      status: schedule?.status,
      createdAt: schedule?.created_at,
      application: {
        id: schedule?.job_applications?.id,
        status: schedule?.job_applications?.status,
        appliedAt: schedule?.job_applications?.applied_at,
        job: {
          id: schedule?.job_applications?.jobs?.id,
          title: schedule?.job_applications?.jobs?.title,
          company: {
            id: schedule?.job_applications?.jobs?.companies?.id,
            name: schedule?.job_applications?.jobs?.companies?.name,
            logoUrl: schedule?.job_applications?.jobs?.companies?.logo_url
          },
          location: {
            city: schedule?.job_applications?.jobs?.company_locations?.city,
            state: schedule?.job_applications?.jobs?.company_locations?.state
          }
        }
      }
    }));
  } catch (error) {
    console.error('Error fetching interview schedules:', error);
    throw error;
  }
};

/**
 * Create a new interview schedule
 */
export const createInterviewSchedule = async (scheduleData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('interview_schedules')?.insert({
        application_id: scheduleData?.applicationId,
        user_id: user?.id,
        interview_type: scheduleData?.interviewType,
        interview_date: scheduleData?.interviewDate,
        interview_duration_minutes: scheduleData?.interviewDurationMinutes || 60,
        location: scheduleData?.location,
        meeting_link: scheduleData?.meetingLink,
        meeting_password: scheduleData?.meetingPassword,
        interviewer_name: scheduleData?.interviewerName,
        interviewer_email: scheduleData?.interviewerEmail,
        interviewer_title: scheduleData?.interviewerTitle,
        preparation_notes: scheduleData?.preparationNotes,
        calendar_provider: scheduleData?.calendarProvider
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      applicationId: data?.application_id,
      interviewType: data?.interview_type,
      interviewDate: data?.interview_date,
      interviewDurationMinutes: data?.interview_duration_minutes,
      location: data?.location,
      meetingLink: data?.meeting_link,
      status: data?.status,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error creating interview schedule:', error);
    throw error;
  }
};

/**
 * Update interview schedule
 */
export const updateInterviewSchedule = async (scheduleId, updates) => {
  try {
    const updateData = {};
    
    if (updates?.interviewDate) updateData.interview_date = updates?.interviewDate;
    if (updates?.interviewType) updateData.interview_type = updates?.interviewType;
    if (updates?.interviewDurationMinutes) updateData.interview_duration_minutes = updates?.interviewDurationMinutes;
    if (updates?.location) updateData.location = updates?.location;
    if (updates?.meetingLink) updateData.meeting_link = updates?.meetingLink;
    if (updates?.meetingPassword) updateData.meeting_password = updates?.meetingPassword;
    if (updates?.interviewerName) updateData.interviewer_name = updates?.interviewerName;
    if (updates?.interviewerEmail) updateData.interviewer_email = updates?.interviewerEmail;
    if (updates?.interviewerTitle) updateData.interviewer_title = updates?.interviewerTitle;
    if (updates?.preparationNotes) updateData.preparation_notes = updates?.preparationNotes;
    if (updates?.interviewNotes) updateData.interview_notes = updates?.interviewNotes;
    if (updates?.outcome) updateData.outcome = updates?.outcome;
    if (updates?.status) updateData.status = updates?.status;
    if (updates?.calendarEventId) updateData.calendar_event_id = updates?.calendarEventId;
    
    updateData.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase?.from('interview_schedules')?.update(updateData)?.eq('id', scheduleId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating interview schedule:', error);
    throw error;
  }
};

/**
 * Delete interview schedule
 */
export const deleteInterviewSchedule = async (scheduleId) => {
  try {
    const { error } = await supabase?.from('interview_schedules')?.delete()?.eq('id', scheduleId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting interview schedule:', error);
    throw error;
  }
};

// =====================================================
// APPLICATION NOTES
// =====================================================

/**
 * Get notes for an application
 */
export const getApplicationNotes = async (applicationId) => {
  try {
    const { data, error } = await supabase?.from('application_notes')?.select('*')?.eq('application_id', applicationId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(note => ({
      id: note?.id,
      applicationId: note?.application_id,
      title: note?.title,
      content: note?.content,
      noteType: note?.note_type,
      createdAt: note?.created_at,
      updatedAt: note?.updated_at
    }));
  } catch (error) {
    console.error('Error fetching application notes:', error);
    throw error;
  }
};

/**
 * Create a new application note
 */
export const createApplicationNote = async (noteData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('application_notes')?.insert({
        application_id: noteData?.applicationId,
        user_id: user?.id,
        title: noteData?.title,
        content: noteData?.content,
        note_type: noteData?.noteType || 'general'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      applicationId: data?.application_id,
      title: data?.title,
      content: data?.content,
      noteType: data?.note_type,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error creating application note:', error);
    throw error;
  }
};

/**
 * Update application note
 */
export const updateApplicationNote = async (noteId, updates) => {
  try {
    const updateData = {
      updated_at: new Date()?.toISOString()
    };
    
    if (updates?.title) updateData.title = updates?.title;
    if (updates?.content) updateData.content = updates?.content;
    if (updates?.noteType) updateData.note_type = updates?.noteType;

    const { data, error } = await supabase?.from('application_notes')?.update(updateData)?.eq('id', noteId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating application note:', error);
    throw error;
  }
};

/**
 * Delete application note
 */
export const deleteApplicationNote = async (noteId) => {
  try {
    const { error } = await supabase?.from('application_notes')?.delete()?.eq('id', noteId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting application note:', error);
    throw error;
  }
};

// =====================================================
// APPLICATION REMINDERS
// =====================================================

/**
 * Get reminders for a user or specific application
 */
export const getApplicationReminders = async (userId, applicationId = null) => {
  try {
    let query = supabase?.from('application_reminders')?.select(`
        *,
        job_applications!inner(
          id,
          status,
          jobs!inner(
            id,
            title,
            companies!inner(
              name
            )
          )
        )
      `)?.eq('user_id', userId)?.order('reminder_date', { ascending: true });

    if (applicationId) {
      query = query?.eq('application_id', applicationId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(reminder => ({
      id: reminder?.id,
      applicationId: reminder?.application_id,
      reminderTitle: reminder?.reminder_title,
      reminderDescription: reminder?.reminder_description,
      reminderType: reminder?.reminder_type,
      reminderDate: reminder?.reminder_date,
      isCompleted: reminder?.is_completed,
      completedAt: reminder?.completed_at,
      notificationSent: reminder?.notification_sent,
      createdAt: reminder?.created_at,
      application: {
        id: reminder?.job_applications?.id,
        status: reminder?.job_applications?.status,
        jobTitle: reminder?.job_applications?.jobs?.title,
        companyName: reminder?.job_applications?.jobs?.companies?.name
      }
    }));
  } catch (error) {
    console.error('Error fetching application reminders:', error);
    throw error;
  }
};

/**
 * Create a new application reminder
 */
export const createApplicationReminder = async (reminderData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('application_reminders')?.insert({
        application_id: reminderData?.applicationId,
        user_id: user?.id,
        reminder_title: reminderData?.reminderTitle,
        reminder_description: reminderData?.reminderDescription,
        reminder_type: reminderData?.reminderType,
        reminder_date: reminderData?.reminderDate
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      applicationId: data?.application_id,
      reminderTitle: data?.reminder_title,
      reminderType: data?.reminder_type,
      reminderDate: data?.reminder_date,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error creating application reminder:', error);
    throw error;
  }
};

/**
 * Update application reminder
 */
export const updateApplicationReminder = async (reminderId, updates) => {
  try {
    const updateData = {
      updated_at: new Date()?.toISOString()
    };
    
    if (updates?.reminderTitle) updateData.reminder_title = updates?.reminderTitle;
    if (updates?.reminderDescription) updateData.reminder_description = updates?.reminderDescription;
    if (updates?.reminderType) updateData.reminder_type = updates?.reminderType;
    if (updates?.reminderDate) updateData.reminder_date = updates?.reminderDate;
    if (typeof updates?.isCompleted === 'boolean') {
      updateData.is_completed = updates?.isCompleted;
      if (updates?.isCompleted) {
        updateData.completed_at = new Date()?.toISOString();
      }
    }

    const { data, error } = await supabase?.from('application_reminders')?.update(updateData)?.eq('id', reminderId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating application reminder:', error);
    throw error;
  }
};

/**
 * Delete application reminder
 */
export const deleteApplicationReminder = async (reminderId) => {
  try {
    const { error } = await supabase?.from('application_reminders')?.delete()?.eq('id', reminderId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting application reminder:', error);
    throw error;
  }
};

// =====================================================
// APPLICATION COMPARISON
// =====================================================

/**
 * Get all comparison sets for a user
 */
export const getComparisonSets = async (userId) => {
  try {
    const { data, error } = await supabase?.from('application_comparison_sets')?.select(`
        *,
        application_comparison_items(count)
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(set => ({
      id: set?.id,
      name: set?.name,
      description: set?.description,
      itemCount: set?.application_comparison_items?.[0]?.count || 0,
      createdAt: set?.created_at,
      updatedAt: set?.updated_at
    }));
  } catch (error) {
    console.error('Error fetching comparison sets:', error);
    throw error;
  }
};

/**
 * Get applications in a comparison set
 */
export const getComparisonSetApplications = async (setId) => {
  try {
    const { data, error } = await supabase?.from('application_comparison_items')?.select(`
        *,
        job_applications!inner(
          id,
          status,
          applied_at,
          jobs!inner(
            id,
            title,
            salary_min,
            salary_max,
            salary_type,
            employment_type,
            experience_level,
            companies!inner(
              id,
              name,
              logo_url
            ),
            company_locations!inner(
              city,
              state
            )
          )
        )
      `)?.eq('comparison_set_id', setId);

    if (error) throw error;

    return data?.map(item => ({
      id: item?.id,
      applicationId: item?.application_id,
      userRating: item?.user_rating,
      pros: item?.pros,
      cons: item?.cons,
      addedAt: item?.added_at,
      application: {
        id: item?.job_applications?.id,
        status: item?.job_applications?.status,
        appliedAt: item?.job_applications?.applied_at,
        job: {
          id: item?.job_applications?.jobs?.id,
          title: item?.job_applications?.jobs?.title,
          salaryMin: item?.job_applications?.jobs?.salary_min,
          salaryMax: item?.job_applications?.jobs?.salary_max,
          salaryType: item?.job_applications?.jobs?.salary_type,
          employmentType: item?.job_applications?.jobs?.employment_type,
          experienceLevel: item?.job_applications?.jobs?.experience_level,
          company: {
            id: item?.job_applications?.jobs?.companies?.id,
            name: item?.job_applications?.jobs?.companies?.name,
            logoUrl: item?.job_applications?.jobs?.companies?.logo_url
          },
          location: {
            city: item?.job_applications?.jobs?.company_locations?.city,
            state: item?.job_applications?.jobs?.company_locations?.state
          }
        }
      }
    }));
  } catch (error) {
    console.error('Error fetching comparison set applications:', error);
    throw error;
  }
};

/**
 * Create a new comparison set
 */
export const createComparisonSet = async (setData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('application_comparison_sets')?.insert({
        user_id: user?.id,
        name: setData?.name,
        description: setData?.description
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error creating comparison set:', error);
    throw error;
  }
};

/**
 * Add application to comparison set
 */
export const addApplicationToComparisonSet = async (setId, applicationId, itemData = {}) => {
  try {
    const { data, error } = await supabase?.from('application_comparison_items')?.insert({
        comparison_set_id: setId,
        application_id: applicationId,
        user_rating: itemData?.userRating,
        pros: itemData?.pros,
        cons: itemData?.cons
      })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding application to comparison set:', error);
    throw error;
  }
};

/**
 * Update comparison item
 */
export const updateComparisonItem = async (itemId, updates) => {
  try {
    const updateData = {};
    
    if (updates?.userRating) updateData.user_rating = updates?.userRating;
    if (updates?.pros) updateData.pros = updates?.pros;
    if (updates?.cons) updateData.cons = updates?.cons;

    const { data, error } = await supabase?.from('application_comparison_items')?.update(updateData)?.eq('id', itemId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating comparison item:', error);
    throw error;
  }
};

/**
 * Remove application from comparison set
 */
export const removeApplicationFromComparisonSet = async (itemId) => {
  try {
    const { error } = await supabase?.from('application_comparison_items')?.delete()?.eq('id', itemId);

    if (error) throw error;
  } catch (error) {
    console.error('Error removing application from comparison set:', error);
    throw error;
  }
};

/**
 * Delete comparison set
 */
export const deleteComparisonSet = async (setId) => {
  try {
    const { error } = await supabase?.from('application_comparison_sets')?.delete()?.eq('id', setId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting comparison set:', error);
    throw error;
  }
};